import { normalizeTimestamp } from './birthEventUtils';
import { formatApptDateTime as _formatApptDateTimeUtil } from './appointmentTimeUtils';
import { getIs24Hour } from './timeFormatPreference';

// ============================================================================
// GESTATIONAL AGE FORMATTING (display only -- no date math)
//
// The backend GestationalAgeService is the single source of truth for GA.
// These helpers format backend-provided values for the UI.
// ============================================================================

/**
 * Format a backend-provided gestational age for display.
 * Accepts null, a pre-formatted string, or { weeks, days } / { totalDays }.
 * This is formatting only -- never performs date math.
 */
export function formatGestationalAge(ga) {
  if (ga === 0) return '0+0';
  if (!ga) return '\u2014';

  if (typeof ga === 'string') return ga;

  if (ga.weeks != null && ga.days != null) {
    return `${ga.weeks}+${ga.days}`;
  }

  if (ga.totalDays != null) {
    const total = Number(ga.totalDays);
    if (!Number.isFinite(total)) return '\u2014';

    const w = Math.floor(total / 7);
    const d = total % 7;
    return `${w}+${d}`;
  }

  return '\u2014';
}

/** Format backend-provided gestational age as an explicit weeks/days label. */
export function formatGestationalAgeWeeksDays(ga) {
  const compact = formatGestationalAge(ga);
  const match = /^(\d+)\+(\d+)$/.exec(compact);
  return match ? `${match[1]}w ${match[2]}d` : compact;
}

/**
 * Format a backend-provided postpartum age for display.
 * Accepts null, a pre-formatted string, or { weeks, days } / { totalDays }.
 */
export function formatPostpartumAge(age) {
  if (age === 0) return '0 days postpartum';
  if (!age) return '\u2014';

  if (typeof age === 'string') return age;

  let totalDays = null;
  if (age.totalDays != null) {
    totalDays = Number(age.totalDays);
  } else if (age.weeks != null) {
    const weeks = Number(age.weeks);
    const days = Number(age.days ?? 0);
    if (Number.isFinite(weeks) && Number.isFinite(days)) {
      totalDays = (weeks * 7) + days;
    }
  }

  if (!Number.isFinite(totalDays) || totalDays < 0) return '\u2014';

  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  if (weeks > 0) return `${weeks}w ${days}d postpartum`;
  return `${totalDays} day${totalDays === 1 ? '' : 's'} postpartum`;
}

// ============================================================================
// TEMPORARY: Due-date calculators (calendar-safe)
//
// Used by usePregnancyManagement.js during pregnancy creation forms.
// Remove once the backend handles due date calculation at form-entry time.
// Uses Date.UTC() for day arithmetic -- 100% timezone-proof.
// ============================================================================

function _addDaysToDateStr(dateStr, daysToAdd) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d) + daysToAdd * 86400000;
  const dt = new Date(ms);
  const ry = dt.getUTCFullYear();
  const rm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const rd = String(dt.getUTCDate()).padStart(2, '0');
  return `${ry}-${rm}-${rd}`;
}

/** @deprecated TEMPORARY -- remove when backend calculates due date */
export function calculateDueDateFromLMP(lmpDate, cycleLengthDays) {
  const cycleLength = Number(cycleLengthDays || 28);
  const cycleAdjustment = Number.isFinite(cycleLength) ? cycleLength - 28 : 0;
  return _addDaysToDateStr(lmpDate, 280 + cycleAdjustment);
}

/** @deprecated TEMPORARY -- remove when backend calculates due date */
export function calculateDueDateFromOvulation(ovulationDate) {
  return _addDaysToDateStr(ovulationDate, 266);
}

/** @deprecated TEMPORARY -- remove when backend calculates due date */
export function calculateDueDateFromConception(conceptionDate) {
  return _addDaysToDateStr(conceptionDate, 266);
}

// ============================================================================
// DATE-ONLY UTILITIES (no timezone conversion)
//
// For C# DateOnly?, PostgreSQL date, and YYYY-MM-DD strings.
// These are calendar dates with no time component.
// NEVER pass through new Date() to avoid UTC-midnight date-shift bugs.
// ============================================================================

// Get today's date in YYYY-MM-DD using local timezone
export function getCurrentLocalDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Extract YYYY-MM-DD from a value that may be "YYYY-MM-DD" or a full ISO datetime.
//
// Strategy:
//   • Plain "YYYY-MM-DD" (no 'T'): returned as-is.
//   • Datetime with timezone offset ("...Z" or "...+HH:MM" / "...-HH:MM"):
//     Parse with new Date() and extract UTC date parts.  The server stores date-only
//     fields at UTC midnight; a timezone-offset response like "2026-03-25T17:00:00-07:00"
//     represents March 26 UTC — UTC parts recover the intended calendar date.
//   • Datetime without timezone info ("...T..."):
//     Split at 'T' — the date part is already the local calendar date.
//   • Date object: use local date parts (the caller already holds a local Date).
export function formatDateOnlyForInput(value) {
  if (!value) return '';
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const str = String(value);
  if (!str.includes('T')) return str;
  // Timezone-offset datetime — parse and use UTC date parts to recover the server's
  // intended calendar date regardless of the client's local timezone.
  if (/Z$|[+-]\d{2}:\d{2}$/.test(str)) {
    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  // No timezone info — the date part before 'T' is the local calendar date.
  return str.split('T')[0];
}

// Format a date-only value as MM/DD/YYYY for display.
// Uses string splitting (not new Date()) to avoid UTC-midnight date-shift.
export function formatDateOnlyForDisplay(value) {
  if (!value) return '';
  const datePart = formatDateOnlyForInput(value);
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return datePart;
  return `${month}/${day}/${year}`;
}

// Format a date-only value as "Mar 30, 2026" for display.
// Uses string splitting (not new Date()) to avoid UTC-midnight date-shift.
const _MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export function formatDateOnlyShort(value) {
  if (!value) return '';
  const datePart = formatDateOnlyForInput(value);
  if (!datePart) return '';
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return datePart;
  return `${_MONTHS_SHORT[month - 1]} ${day}, ${year}`;
}

// ============================================================================
// TIMESPAN UTILITIES (no timezone conversion)
//
// For C# TimeSpan?, TimeOnly?, and string time fields.
// Values are "HH:mm:ss" or "HH:mm" -- clock times with no date/timezone.
// These must NEVER be timezone-shifted.
// ============================================================================

// Get current local time as HH:mm or HH:mm:ss
export function getCurrentLocalTime(withSeconds = false) {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  if (withSeconds) {
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }
  return `${hours}:${minutes}`;
}

// Normalize a TimeSpan value for <input type="time">.
// Accepts "HH:mm:ss", "HH:mm", or a full datetime string (extracts local time portion).
// Returns "HH:mm" or "HH:mm:ss" (depending on withSeconds), or '' for empty.
// For bare time strings (no 'T'), passes through WITHOUT timezone conversion.
export function formatTimeSpanForInput(value, withSeconds = false) {
  if (!value) return '';
  const str = String(value).trim();

  // Bare time: HH:mm or HH:mm:ss -- pass through, no timezone shift
  if (/^\d{2}:\d{2}$/.test(str)) {
    return withSeconds ? `${str}:00` : str;
  }
  if (/^\d{2}:\d{2}:\d{2}$/.test(str)) {
    return withSeconds ? str : str.slice(0, 5);
  }

  // Full datetime string -- normalize first (handles space-separated PostgreSQL format,
  // offset format, and T-separated format), then extract local time
  const normalized = normalizeTimestamp(str);
  if (normalized && normalized !== str) {
    try {
      const dt = new Date(normalized);
      if (!isNaN(dt.getTime())) {
        const h = String(dt.getHours()).padStart(2, '0');
        const m = String(dt.getMinutes()).padStart(2, '0');
        const s = String(dt.getSeconds()).padStart(2, '0');
        return withSeconds ? `${h}:${m}:${s}` : `${h}:${m}`;
      }
    } catch {
      // fall through
    }
  }

  // Try parsing as-is (e.g. already has Z suffix)
  try {
    const dt = new Date(str);
    if (!isNaN(dt.getTime()) && str.length > 10) {
      const h = String(dt.getHours()).padStart(2, '0');
      const m = String(dt.getMinutes()).padStart(2, '0');
      const s = String(dt.getSeconds()).padStart(2, '0');
      return withSeconds ? `${h}:${m}:${s}` : `${h}:${m}`;
    }
  } catch {
    // fall through
  }

  return '';
}

// Normalize a TimeSpan value for backend submission.
// Ensures "HH:mm:ss" format. Returns null for empty/invalid.
export function formatTimeSpanForSubmit(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (/^\d{2}:\d{2}$/.test(str)) return `${str}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(str)) return str;
  return null;
}

// Format a recorded clock time using the user's 12/24-hour display preference.
// Bare TimeSpan values are not instants: never apply a timezone conversion.
export function formatTimeSpanForDisplay(value, is24Hour = getIs24Hour()) {
  if (!value) return '';
  let hours;
  let minutes;
  if (value instanceof Date) {
    hours = value.getHours();
    minutes = value.getMinutes();
  } else {
    const match = /^(\d{2}):(\d{2})(?::[0-5]\d)?$/.exec(String(value).trim());
    if (!match) return '';
    hours = Number(match[1]);
    minutes = Number(match[2]);
  }
  if (!Number.isFinite(hours) || hours > 23 || !Number.isFinite(minutes) || minutes > 59) return '';
  const minuteText = String(minutes).padStart(2, '0');
  return is24Hour
    ? `${String(hours).padStart(2, '0')}:${minuteText}`
    : `${hours % 12 || 12}:${minuteText} ${hours < 12 ? 'AM' : 'PM'}`;
}

// ============================================================================
// DATETIME UTILITIES (UTC ↔ local conversion)
//
// For C# DateTime/DateTime?, PostgreSQL timestamp/timestamptz.
// These represent a point in time and need UTC→local conversion for display
// and local→UTC conversion for storage.
// ============================================================================

// Get current datetime in datetime-local format (YYYY-MM-DDTHH:mm)
export function getCurrentDateTimeLocal() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Combine local date (YYYY-MM-DD) and time (HH:mm or HH:mm:ss) into a UTC ISO string.
// Treats the inputs as local time and converts to UTC for backend storage.
export function toUTCISOFromParts(date, time) {
  if (!date || !time) return null;
  try {
    const localDateTime = `${date}T${time}`;
    const dt = new Date(localDateTime);
    if (isNaN(dt.getTime())) return null;
    return dt.toISOString();
  } catch {
    return null;
  }
}

// Convert ISO/UTC datetime string to datetime-local format (YYYY-MM-DDTHH:mm)
// Converts from UTC to local timezone
export function formatDateTimeLocalForInput(isoString) {
  if (!isoString) return '';
  try {
    const valueToProcess = normalizeTimestamp(isoString);
    const date = new Date(valueToProcess);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    console.error('Error converting ISO to datetime-local:', isoString, e);
    return '';
  }
}

export const toLocalDateTimeInput = formatDateTimeLocalForInput;

// Convert datetime-local format (YYYY-MM-DDTHH:mm) to ISO string.
// datetime-local is local timezone; toISOString() converts to UTC.
export function convertDateTimeLocalToISO(dateTimeLocal) {
  if (!dateTimeLocal) return null;
  try {
    const date = new Date(dateTimeLocal);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
  } catch (e) {
    console.error('Error converting datetime-local to ISO:', dateTimeLocal, e);
    return null;
  }
}

// Format a UTC/ISO datetime for user-facing display in 24-hour format.
// Example output: "Feb 8, 2026, 14:32"
export function formatDateTimeDisplay(isoString) {
  if (!isoString) return '';
  try {
    const valueToProcess = normalizeTimestamp(isoString);
    const date = new Date(valueToProcess);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error('Error formatting datetime for display:', isoString, e);
    return '';
  }
}

// Parse a datetime value (ISO/UTC) and return separate local date and time strings.
// For full datetime strings (contains 'T'): converts UTC→local.
// For time-only strings (contains ':'): returns as-is WITHOUT timezone shift (it's a TimeSpan).
// For date-only strings: returns date as-is with empty time.
// Returns { date: 'YYYY-MM-DD', time: 'HH:mm' }
export function parseDateTimeValue(value, fallbackDate) {
  if (!value) return { date: '', time: '' };

  // Convert Date objects and numbers to string (BirthSummaryForm may pass Date from calculateROMData/calculateLaborStages)
  let valueStr;
  if (value instanceof Date) {
    valueStr = isNaN(value.getTime()) ? '' : value.toISOString();
  } else if (typeof value === 'number' && !isNaN(value)) {
    valueStr = new Date(value).toISOString();
  } else {
    valueStr = value;
  }
  if (!valueStr || typeof valueStr !== 'string') return { date: '', time: '' };

  const normalizedValue = normalizeTimestamp(valueStr);
  const strToCheck = typeof normalizedValue === 'string' ? normalizedValue : valueStr;

  // Full datetime format -- convert UTC to local
  if (strToCheck && strToCheck.includes('T')) {
    const dateObj = new Date(strToCheck);

    if (isNaN(dateObj.getTime())) {
      const [datePart, timePart] = strToCheck.split('T');
      return {
        date: datePart || '',
        time: timePart ? timePart.substring(0, 8) : ''
      };
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}:${seconds}`
    };
  }

  // Time-only format -- return as-is, NO timezone shift (it's a TimeSpan)
  if (typeof valueStr === 'string' && valueStr.includes(':') && !valueStr.includes('T')) {
    const dateToUse = fallbackDate ? (typeof fallbackDate === 'string' ? fallbackDate.split('T')[0] : '') : '';
    return {
      date: dateToUse,
      time: valueStr.length >= 8 ? valueStr.substring(0, 8) : valueStr.substring(0, 5)
    };
  }

  // Date-only
  return { date: typeof valueStr === 'string' ? valueStr : '', time: '' };
}

// Extract date and time from backend record (handles both separate and combined DateTime fields).
// Converts UTC datetime from backend to local timezone.
// Returns empty strings when values are missing (callers decide defaults).
export function extractDateAndTime(record) {
  const dateField = record.date || record.Date || '';
  const timeField = record.time || record.Time || '';

  let date = '';
  let time = '';

  if (dateField && dateField !== '0001-01-01T00:00:00') {
    if (dateField.includes('T')) {
      const localDT = formatDateTimeLocalForInput(dateField);
      if (localDT && localDT.includes('T')) {
        const [datePart, timePart] = localDT.split('T');
        date = datePart;
        time = timePart;
      } else {
        date = dateField.split('T')[0];
      }
    } else {
      date = dateField;
    }
  }

  if (!time && timeField && timeField !== '00:00:00') {
    time = formatTimeSpanForInput(timeField);
  }

  return { date, time };
}

// ============================================================================
// APPOINTMENT UTILITIES
// ============================================================================

// Format appointment date and time for display.
// Delegates to the shared appointmentTimeUtils utility so output is deterministic
// and includes the timezone abbreviation (e.g. "Wednesday, April 15, 2026 at 2:00 PM EDT").
export function formatAppointmentDateTime(appointment) {
  try {
    if (!appointment.startTime) {
      return 'No time specified';
    }

    const utcStr = normalizeTimestamp(appointment.startTime);
    const date = new Date(utcStr);

    if (isNaN(date.getTime())) {
      console.error('Invalid date for appointment:', appointment);
      return 'Invalid Date';
    }

    return _formatApptDateTimeUtil(utcStr, appointment.timeZoneId);
  } catch (err) {
    console.error('Error formatting appointment date:', appointment, err);
    return 'Invalid Date';
  }
}

export function isUpcomingAppointment(appointment) {
  const now = new Date();
  if (!appointment.startTime) return false;

  const valueToProcess = normalizeTimestamp(appointment.startTime);
  const appointmentDate = new Date(valueToProcess);

  if (isNaN(appointmentDate.getTime())) {
    console.error('Invalid startTime for appointment:', appointment);
    return false;
  }

  return appointmentDate > now;
}

export function organizeAppointmentsByDate(appointments) {
  const now = new Date();
  const upcoming = [];
  const past = [];

  appointments.forEach(appointment => {
    if (!appointment.startTime) return;

    const valueToProcess = normalizeTimestamp(appointment.startTime);
    const appointmentDate = new Date(valueToProcess);

    if (isNaN(appointmentDate.getTime())) {
      console.error('Invalid startTime for appointment:', appointment);
      return;
    }

    if (appointmentDate > now) {
      upcoming.push(appointment);
    } else {
      past.push(appointment);
    }
  });

  const parseDate = (timeValue) => new Date(normalizeTimestamp(timeValue));

  upcoming.sort((a, b) => parseDate(a.startTime) - parseDate(b.startTime));
  past.sort((a, b) => parseDate(b.startTime) - parseDate(a.startTime));

  return { upcoming, past };
}

// ============================================================================
// DISPLAY UTILITIES (UTC→local, 24-hour)
// ============================================================================

// Convert UTC/ISO datetime string to local 24-hour time display (HH:mm).
export function toLocalTimeDisplay(isoString) {
  if (!isoString) return '';
  try {
    const valueToProcess = normalizeTimestamp(isoString);
    const date = new Date(valueToProcess);
    if (isNaN(date.getTime())) return '';

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    console.error('Error converting to local time display:', isoString, e);
    return '';
  }
}

// ============================================================================
// LEGACY COMPATIBILITY (existing exports, fixed)
// ============================================================================

// Format date for display -- extracts YYYY-MM-DD via string splitting (safe for date-only)
export function formatDateForDisplay(dateString) {
  if (!dateString) return '';
  return formatDateOnlyForInput(dateString);
}

// Format as MM/DD/YYYY
export function formatDateMDY(dateString) {
  if (!dateString) return '';
  return formatDateOnlyForDisplay(dateString);
}

// Format date for HTML date input (safe string-splitting, no new Date() for date-only)
export function formatDateForInput(dateValue) {
  if (!dateValue) return '';
  return formatDateOnlyForInput(dateValue);
}

// Format time for HTML time input (handles TimeSpan format).
// Returns '' for empty/falsy values (no longer silently defaults to current time).
export function formatTimeForInput(timeValue) {
  if (!timeValue) return '';
  return formatTimeSpanForInput(timeValue);
}
